import { defineStore } from 'pinia'
import axios from '../axios.js'
import { sessionState, markSessionExpired } from '../session/sessionExpiry.js'

export const useUserStore = defineStore('users', {
  state: () => ({
    users: [],
    packages: [
      {
        id: 'monthly',
        name: 'Monthly Package',
        price: 299,
        gamesIncluded: 120,
        features: [
          'Access to 120 games per month',
          'Basic API Access',
          'Standard Support',
          'Real-time Updates'
        ],
        leagueAccess: ['nba', 'wnba', 'euroleague'],
        apiLimit: 5000,
        extraGamePrice: 12
      },
      {
        id: 'pay-per-game',
        name: 'Pay Per Game',
        price: 12,
        gamesIncluded: 1,
        features: [
          'Pay as you go',
          'Basic API Access',
          'Standard Support',
          'Real-time Updates'
        ],
        leagueAccess: ['nba', 'wnba', 'euroleague'],
        apiLimit: 100,
        minimumPurchase: 1
      }
    ],
    availablePermissions: {},
    roles: [
      {
        id: 'client',
        name: 'Client',
        permissions: ['view_fixtures', 'view_odds'] // Default permissions for new clients
      },
      {
        id: 'trader',
        name: 'Trader',
        permissions: ['view_fixtures', 'view_odds', 'edit_odds', 'manage_risk'] // Default permissions for new traders
      },
      {
        id: 'admin',
        name: 'ProductAdminOskar',
        permissions: ['oskar'] // Default permissions for new admins
      },
      {
        id: 'superadmin',
        name: 'SuperAdmin',
        permissions: ['*'] // Default permissions for new admins
      }
    ],
    loading: false,
    error: null,
    currentUser: null,
    isAuthenticated: false,
    token: null
  }),

  getters: {
    getByRole: (state) => (role) => {
      return state.users.filter(user => user.role === role)
    },
    getActiveUsers: (state) => {
      return state.users.filter(user => user.status === 'active')
    }
  },

  actions: {
    async fetchUsers() {
      this.loading = true
      try {
        const response = await axios.get('/api/users')
        this.users = response.data.map(user => ({
          ...user,
          permissions: user.permissions || [], // Ensure permissions is always an array
          lastActive: user.last_active || new Date().toISOString()
        }))
      } catch (error) {
        console.error('Failed to fetch users:', error)
        this.error = error.message
      } finally {
        this.loading = false
      }
    },

    async fetchAvailablePermissions() {
      try {
        const response = await axios.get('/api/permissions')
        // Handle both old format and new database format
        if (response.data.detailed_permissions) {
          this.availablePermissions = response.data.detailed_permissions
        } else if (response.data.permissions) {
          this.availablePermissions = response.data.permissions
        } else {
          this.availablePermissions = response.data
        }
        return this.availablePermissions
      } catch (error) {
        console.error('Failed to fetch permissions:', error)
        return []
      }
    },

    async createUser(userData) {
      try {
        const response = await axios.post('/api/create-user', userData)
        const newUser = {
          ...response.data.user,
          permissions: userData.permissions || this.getDefaultPermissionsForRole(userData.role),
          lastActive: new Date().toISOString()
        }
        this.users.push(newUser)
        return newUser
      } catch (error) {
        console.error('Failed to create user:', error)
        throw error
      }
    },

    async updateUser(userId, userData) {
      try {
        const response = await axios.put(`/api/users/${userId}`, userData)
        const index = this.users.findIndex(u => u.id === userId)
      if (index !== -1) {
          this.users[index] = {
            ...this.users[index],
            ...response.data.user,
            permissions: response.data.user.permissions || userData.permissions || []
      }
        }
        return response.data.user
      } catch (error) {
        console.error('Failed to update user:', error)
        throw error
      }
    },

    async login(credentials) {
      try {
        // Try real authentication first
        const response = await axios.post('/api/login', credentials)
        
        if (response.data.user && response.data.token) {
          this.currentUser = {
            ...response.data.user,
            permissions: response.data.user.permissions || this.getDefaultPermissionsForRole(response.data.user.role)
          }
          this.isAuthenticated = true
          this.token = response.data.token
          
          // Store token in localStorage
          localStorage.setItem('auth_token', response.data.token)
          
          // Store token type information for dual-token strategy
          if (response.data.token_type) {
            localStorage.setItem('token_type', response.data.token_type)
          }
          if (response.data.expires_at) {
            localStorage.setItem('token_expires_at', response.data.expires_at)
          }
          
          return { success: true, user: this.currentUser }
        }
      } catch (error) {
        console.warn('Real authentication failed, trying demo login:', error)
        
        // Demo login fallback
        if (credentials.email === 'admin@oddscomposer.io' && credentials.password === 'admin123') {
          this.currentUser = {
            id: 1,
            name: 'Demo Admin',
            email: 'admin@oddscomposer.io',
            role: 'admin',
            status: 'active',
            permissions: ['*']
          }
          this.isAuthenticated = true
          this.token = 'demo_token_admin'
          localStorage.setItem('auth_token', 'demo_token_admin')
          localStorage.setItem('token_type', 'web')
          localStorage.setItem('token_expires_at', new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())
          return { success: true, user: this.currentUser }
        }
        
        if (credentials.email === 'trader@oddscomposer.io' && credentials.password === 'trader123') {
          this.currentUser = {
            id: 2,
            name: 'Demo Trader',
            email: 'trader@oddscomposer.io',
            role: 'trader',
            status: 'active',
            permissions: ['view_fixtures', 'view_odds', 'edit_odds', 'manage_risk']
          }
            this.isAuthenticated = true
          this.token = 'demo_token_trader'
          localStorage.setItem('auth_token', 'demo_token_trader')
          localStorage.setItem('token_type', 'web')
          localStorage.setItem('token_expires_at', new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString())
          return { success: true, user: this.currentUser }
        }
        
        throw new Error('Invalid credentials')
      }
    },

    async logout() {
      try {
        await axios.post('/api/logout')
      } catch (error) {
        console.warn('Logout request failed:', error)
      } finally {
        this.currentUser = null
        this.isAuthenticated = false
        this.token = null
        localStorage.removeItem('auth_token')
      }
    },

    async checkAuth() {
      const token = localStorage.getItem('auth_token')
      if (!token) {
        // Keep the flag in sync, or the /login ↔ / guards redirect each other forever
        this.isAuthenticated = false
        return false
      }

      // Handle demo tokens
      if (token.startsWith('demo_token_')) {
        if (token === 'demo_token_admin') {
          this.currentUser = {
            id: 1,
            name: 'Demo Admin',
            email: 'admin@oddscomposer.io',
            role: 'admin',
            status: 'active',
            permissions: ['*']
          }
          this.isAuthenticated = true
          this.token = token
          return true
        }
        if (token === 'demo_token_trader') {
          this.currentUser = {
            id: 2,
            name: 'Demo Trader',
            email: 'trader@oddscomposer.io',
            role: 'trader',
            status: 'active',
            permissions: ['view_fixtures', 'view_odds', 'edit_odds', 'manage_risk']
          }
          this.isAuthenticated = true
          this.token = token
          return true
        }
      }

      try {
        const response = await axios.get('/api/user')
        this.currentUser = {
          ...response.data,
          permissions: response.data.permissions || this.getDefaultPermissionsForRole(response.data.role)
        }
        this.isAuthenticated = true
        this.token = token
        return true
      } catch (error) {
        console.error('Auth check failed:', error)
        const status = error.response?.status
        const tokenRejected = status === 401 || (status === 404 && !error.response?.data)
        // Expired while using the console: keep the token and page, show the re-login popup
        if (tokenRejected && this.isAuthenticated && this.currentUser) {
          markSessionExpired()
          if (sessionState.expired) return false
        }
        localStorage.removeItem('auth_token')
        this.isAuthenticated = false
        this.currentUser = null
        this.token = null
        return false
      }
    },

    updateUserRole(userId, role) {
      const user = this.users.find(u => u.id === userId)
      if (user) {
        user.role = role
        // Keep existing permissions or set default ones
        if (!user.permissions || user.permissions.length === 0) {
          user.permissions = this.getDefaultPermissionsForRole(role)
        }
      }
    },

    getDefaultPermissionsForRole(role) {
      const roleObj = this.roles.find(r => r.id === role)
      return roleObj ? [...roleObj.permissions] : []
    },

    // Permission helper methods
    userHasPermission(userId, permission) {
      const user = this.users.find(u => u.id === userId) || this.currentUser
      if (!user || !user.permissions) return false
      
      // If user has '*' permission, they have all permissions
      if (user.permissions.includes('*')) return true
      
      return user.permissions.includes(permission)
    },

    userHasAnyPermission(userId, permissions) {
      const user = this.users.find(u => u.id === userId) || this.currentUser
      if (!user || !user.permissions) return false
      
      // If user has '*' permission, they have all permissions
      if (user.permissions.includes('*')) return true
      
      return permissions.some(permission => user.permissions.includes(permission))
    },

    currentUserHasPermission(permission) {
      if (!this.currentUser || !this.currentUser.permissions) return false
      
      // If user has '*' permission, they have all permissions
      if (this.currentUser.permissions.includes('*')) return true
      
      return this.currentUser.permissions.includes(permission)
    },

    getCurrentUserPermissions() {
      return this.currentUser?.permissions || []
    }
  }
}) 